import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import Reveal from "@/components/base/Reveal";
import usePageMeta from "@/hooks/usePageMeta";
import { profile } from "@/mocks/profile";
import { works } from "@/mocks/works";

const representativeIds = ["letters-of-the-tide", "white-corridor", "silent-testimony", "night-train"];

export default function Profile() {
  usePageMeta({
    title: "翔田千里个人资料－演员经历、代表作与荣誉记录",
    description:
      "翔田千里个人资料页，包含出生信息、职业经历、代表作品、作品年表与荣誉记录，并附表演方法说明，便于影迷与研究者全面了解这位影视演员。",
    keywords: "翔田千里个人资料,翔田千里简介,演员经历,代表作品,荣誉记录",
  });

  const representativeWorks = representativeIds
    .map((id) => works.find((work) => work.id === id))
    .filter((work): work is (typeof works)[number] => Boolean(work));

  return (
    <SiteLayout>
      <section className="border-b border-background-200/70 bg-background-50">
        <div className="container-page py-9 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "个人资料" }]} />
          <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-heading text-3xl leading-tight text-foreground-950 md:text-[40px]">
                翔田千里个人资料
              </h1>
              <p className="mt-3 text-sm tracking-wide text-foreground-500">
                {profile.nameKana} · {profile.nameEn}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-md bg-primary-50 px-3 py-2 text-xs text-primary-700">{profile.tagline}</span>
              <span className="rounded-md bg-accent-50 px-3 py-2 text-xs text-accent-800">1988 年出生</span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-8 md:py-12">
        <div className="container-page grid grid-cols-1 gap-8 lg:grid-cols-[380px_1fr] lg:gap-12">
          <Reveal>
            <div className="relative">
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-lg bg-primary-900">
                <img
                  src={profile.portrait}
                  alt="翔田千里个人写真"
                  title="翔田千里 演员个人资料写真"
                  className="h-full w-full object-cover object-top"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary-950/80 to-transparent p-4 pt-12">
                  <p className="font-heading text-lg text-background-50">{profile.name}</p>
                  <p className="mt-1 text-[11px] text-background-50/70">{profile.tagline}</p>
                </div>
              </div>
              <div className="absolute -left-2 top-5 rounded-md bg-accent-500 px-3 py-2 text-primary-950">
                <p className="text-[10px] tracking-wide">出道</p>
                <p className="font-heading text-lg leading-none">2010</p>
              </div>
            </div>
          </Reveal>

          <div>
            <div className="flex items-center gap-2">
              <span className="h-px w-7 bg-accent-500"></span>
              <span className="text-[11px] uppercase tracking-wider2 text-accent-600">Biography</span>
            </div>
            <h2 className="mt-3 font-heading text-2xl text-foreground-950">以克制见长的影视演员</h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700">{profile.intro}</p>
            <p className="mt-3 text-sm leading-loose text-foreground-600">{profile.careerIntro}</p>

            <div className="mt-7 rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h3 className="text-[11px] uppercase tracking-wider2 text-foreground-400">基本资料</h3>
              <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {profile.facts.map((fact) => (
                  <div key={fact.label} className="flex items-center justify-between gap-3 border-b border-background-200/60 pb-2">
                    <dt className="shrink-0 text-xs text-foreground-400">{fact.label}</dt>
                    <dd className="text-right text-xs text-foreground-900">{fact.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-6">
              <h3 className="text-[11px] uppercase tracking-wider2 text-foreground-400">代表作品</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {profile.representativeWorks.map((title) => (
                  <span
                    key={title}
                    className="rounded-full border border-background-300/70 bg-background-50 px-3 py-1 text-xs text-foreground-800"
                  >
                    《{title}》
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-page">
          <div className="flex items-center gap-2">
            <span className="h-px w-7 bg-accent-500"></span>
            <span className="text-[11px] uppercase tracking-wider2 text-accent-600">Career</span>
          </div>
          <h2 className="mt-3 font-heading text-2xl text-foreground-950 md:text-[30px]">职业经历年表</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            从独立电影到电影与剧集双线发展，以下节点按年份整理，可与作品列表交叉查看。
          </p>

          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
            {profile.timeline.map((item, index) => (
              <Reveal key={item.year} delay={index * 50}>
                <div className="flex h-full gap-4 rounded-lg border border-background-200/80 bg-background-100/60 p-5">
                  <div className="flex flex-col items-center">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-600 font-heading text-xs text-background-50">
                      {item.year}
                    </span>
                    <span className="mt-2 w-px flex-1 bg-background-300/70"></span>
                  </div>
                  <div>
                    <h3 className="font-heading text-base text-foreground-950">{item.title}</h3>
                    <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.description}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="container-page grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr] lg:gap-10">
          <div>
            <div className="flex items-center gap-2">
              <span className="h-px w-7 bg-accent-500"></span>
              <span className="text-[11px] uppercase tracking-wider2 text-accent-600">Honors</span>
            </div>
            <h2 className="mt-3 font-heading text-2xl text-foreground-950">荣誉与奖项</h2>
            <div className="mt-5 overflow-hidden rounded-lg border border-background-200/80 bg-background-50">
              {profile.honors.map((honor) => (
                <div
                  key={`${honor.year}-${honor.title}`}
                  className="flex items-start gap-4 border-b border-background-200/60 p-4 last:border-b-0"
                >
                  <span className="shrink-0 rounded bg-accent-100 px-2 py-1 font-heading text-[11px] text-accent-900">
                    {honor.year}
                  </span>
                  <div>
                    <p className="text-sm text-foreground-900">{honor.title}</p>
                    <p className="mt-1 text-[11px] text-foreground-500">{honor.work}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-foreground-400">
              荣誉记录整理自公开报道，仅作资料展示之用。
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2">
              <span className="h-px w-7 bg-accent-500"></span>
              <span className="text-[11px] uppercase tracking-wider2 text-accent-600">Method</span>
            </div>
            <h2 className="mt-3 font-heading text-2xl text-foreground-950">表演方法三则</h2>
            <div className="mt-5 space-y-3">
              {profile.approach.map((item, index) => (
                <div key={item.title} className="rounded-lg border border-background-200/80 bg-background-50 p-4">
                  <div className="flex items-center gap-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded bg-primary-100 text-[11px] text-primary-700">
                      0{index + 1}
                    </span>
                    <h3 className="font-heading text-sm text-foreground-950">{item.title}</h3>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-page">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-heading text-2xl text-foreground-950">代表作品</h2>
              <p className="mt-2 text-xs text-foreground-500">四部不同阶段的代表作，点击卡片可进入作品详情。</p>
            </div>
            <Link
              to="/works"
              className="inline-flex items-center gap-2 rounded-md border border-background-300/80 px-4 py-2 text-xs text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              全部作品
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-4">
            {representativeWorks.map((work, index) => (
              <Reveal key={work.id} delay={index * 50}>
                <WorkCard work={work} compact />
              </Reveal>
            ))}
          </div>

          <div className="mt-10 flex flex-col items-center justify-between gap-4 rounded-lg bg-primary-950 p-6 md:flex-row md:p-8">
            <div>
              <h2 className="font-heading text-xl text-background-50">继续浏览</h2>
              <p className="mt-2 text-xs leading-relaxed text-background-50/70">
                回到首页查看最新焦点内容，或直接进入作品库按类型与年份筛选。
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-home-4-line" aria-hidden="true"></i>
                返回首页
              </Link>
              <Link
                to="/works"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-xs text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-film-line" aria-hidden="true"></i>
                影视作品
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}